import React, { useEffect, useState } from "react";
import API, { CONFIG, UPDATE_CONFIG } from "../../network/api";
import { TEXT_DEF } from "../../constant/text";
import { Button, Switch, DatePicker, TimePicker, Tabs, InputNumber } from "antd";
import ModalCommon from "../../components/Modal";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TEXT = {
    onOffGame: "Bật tắt game",
    turnOnOff: "Tắt/Mở",
    eventStartTime: "Thời gian bắt đầu sự kiện",
    eventEndTime: "Thời gian kết thúc sự kiện",
    daysDisplayWeek: "Ngày hiển thị trong tuần",
    dtcResetDay: "Ngày reset DTC",
    time: "Thời gian",
    frequency: "Tần suất",
    monthly: "Hằng tháng",
    weekly: "Hằng tuần",
    daily: "Hằng ngày",
    resetDay: "Ngày reset",
    hour: "Giờ",
}

const DAYS_SHORT = [
    { value: "1", label: "T2" },
    { value: "2", label: "T3" },
    { value: "3", label: "T4" },
    { value: "4", label: "T5" },
    { value: "5", label: "T6" },
    { value: "6", label: "T7" },
    { value: "0", label: "CN" },
];

const OnOffGame = () => {
    const [configGame, setConfigGame] = useState();
    const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
    const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);

    const [dateFrom, setDateFrom] = useState();
    const [dateTo, setDateTo] = useState();
    const [isShowGame, setIsShowGame] = useState(false); // isGameEnabled from API
    const [displayDaysWithTime, setDisplayDaysWithTime] = useState([]); // Array of {day: "1", time: dayjs(...)}
    const [selectedResetDays, setSelectedResetDays] = useState([]); // Array of {day: "1", time: "23:59:00"}
    const [resetFrequency, setResetFrequency] = useState("monthly"); // "monthly", "weekly", "daily"
    const [resetDay, setResetDay] = useState(1); // 1-31 for monthly
    const [resetTime, setResetTime] = useState(dayjs("00:00:00", "HH:mm:ss")); // Time for reset

    useEffect(() => {
        getDataConfig();
    }, []);

    const getDataConfig = async () => {
        try {
            const res = await API.get(`${CONFIG}`);
        // API response format: { success: true, message: "...", data: {...}ơ]ơ-}
            const responseData = res.data?.data || res.data;
            setConfigGame({
                isGameEnabled: responseData.isGameEnabled,
                eventStart: responseData.eventStart,
                eventEnd: responseData.eventEnd,
                displayDays: responseData.displayDays || [],
                resetDays: responseData.resetDays || []
            });
            // Set isGameEnabled (toggle switch state)
            setIsShowGame(responseData.isGameEnabled === true || responseData.isGameEnabled === false ? responseData.isGameEnabled : false);
            
            // Set eventStart and eventEnd dates
            if (responseData.eventStart) {
                const eventStartDate = dayjs(responseData.eventStart).format('YYYY-MM-DD');
                setDateFrom(eventStartDate);
            }
            if (responseData.eventEnd) {
                const eventEndDate = dayjs(responseData.eventEnd).format('YYYY-MM-DD');
                setDateTo(eventEndDate);
            }
            
            // Handle displayDays - read from gameOpenSchedule structure
            // Structure: { days: [{ day: 6, time: { hour: 8, minute: 0, second: 0 } }, ...] }
            const gameOpenSchedule = responseData.schedule?.gameOpenSchedule || responseData.gameOpenSchedule;
            if (gameOpenSchedule && gameOpenSchedule.days && Array.isArray(gameOpenSchedule.days)) {
                const days = gameOpenSchedule.days;
                
                // Read format: [{day: 6, time: {hour: 8, minute: 0, second: 0}}, ...]
                const displayDays = days.map(d => {
                    const dayStr = String(d.day);
                    const hour = d.time?.hour !== undefined && d.time?.hour !== null ? d.time.hour : 0;
                    const minute = d.time?.minute !== undefined && d.time?.minute !== null ? d.time.minute : 0;
                    const second = d.time?.second !== undefined && d.time?.second !== null ? d.time.second : 0;
                    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
                    return { day: dayStr, time: dayjs(timeStr, "HH:mm:ss") };
                });
                setDisplayDaysWithTime(displayDays);
            } else {
                // Fallback: empty array if no schedule found
                setDisplayDaysWithTime([]);
            }
            // Set reset DTC config from resetSchedule
            // Check both schedule.resetSchedule and resetSchedule (for backward compatibility)
            const resetSchedule = responseData.schedule?.resetSchedule || responseData.resetSchedule;
            if (resetSchedule) {
                // Map type: "WEEKLY" -> "weekly", "MONTHLY" -> "monthly", "DAILY" -> "daily"
                const typeUpper = resetSchedule.type?.toUpperCase() || "MONTHLY";
                let frequency = "monthly"; // default
                if (typeUpper === "WEEKLY") {
                    frequency = "weekly";
                } else if (typeUpper === "DAILY") {
                    frequency = "daily";
                } else if (typeUpper === "MONTHLY") {
                    frequency = "monthly";
                }
                setResetFrequency(frequency);
                
                // Set dayOfWeek (for WEEKLY) or day (for MONTHLY)
                // For WEEKLY: use dayOfWeek (1-7, where 1=Monday)
                // For MONTHLY: use day (1-31) or dayOfWeek if day doesn't exist
                if (frequency === "weekly" && resetSchedule.dayOfWeek !== undefined) {
                    setResetDay(resetSchedule.dayOfWeek);
                } else if (frequency === "monthly") {
                    setResetDay(resetSchedule.day !== undefined ? resetSchedule.day : (resetSchedule.dayOfWeek || 1));
                } else {
                    // For DAILY, day doesn't matter, but keep it for UI
                    setResetDay(1);
                }
                
                // Set time from resetSchedule.time object
                if (resetSchedule.time) {
                    const hour = resetSchedule.time.hour !== undefined && resetSchedule.time.hour !== null ? resetSchedule.time.hour : 0;
                    const minute = resetSchedule.time.minute !== undefined && resetSchedule.time.minute !== null ? resetSchedule.time.minute : 0;
                    const second = resetSchedule.time.second !== undefined && resetSchedule.time.second !== null ? resetSchedule.time.second : 0;
                    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
                    setResetTime(dayjs(timeStr, "HH:mm:ss"));
                } else {
                    setResetTime(dayjs("00:00:00", "HH:mm:ss"));
                }
            } else {
                // Fallback to old format
                const oldFrequency = responseData.resetFrequency || "monthly";
                setResetFrequency(oldFrequency);
                setResetDay(responseData.resetDay || 1);
                setResetTime(responseData.resetTime ? dayjs(responseData.resetTime, "HH:mm:ss") : dayjs("00:00:00", "HH:mm:ss"));
            }
        } catch (err) {
            // Error handling
        }
    };

    const handleUpdateConfig = async () => {
        try {
            const body = {
                config: {
                    isGameEnabled: isShowGame !== undefined ? isShowGame : configGame?.isGameEnabled || false, // Map toggle switch to isGameEnabled
                    eventStart: dateFrom ? formatRequest(dateFrom) : configGame?.eventStart,
                    eventEnd: dateTo ? formatRequest(dateTo) : configGame?.eventEnd,
                    schedule: {
                        gameOpenSchedule: {
                            days: displayDaysWithTime.map(d => ({
                                day: parseInt(d.day),
                                time: {
                                    hour: d.time ? d.time.hour() : 0,
                                    minute: d.time ? d.time.minute() : 0,
                                    second: d.time ? d.time.second() : 0
                                }
                            }))
                        },
                        resetSchedule: {
                            type: resetFrequency.toUpperCase(), // "WEEKLY", "MONTHLY", "DAILY"
                            ...(resetFrequency === "weekly" && { dayOfWeek: resetDay }), // For WEEKLY: dayOfWeek (1-7)
                            ...(resetFrequency === "monthly" && { day: resetDay }), // For MONTHLY: day (1-31)
                            // For DAILY: no dayOfWeek or day needed
                            time: {
                                hour: resetTime ? resetTime.hour() : 0,
                                minute: resetTime ? resetTime.minute() : 0,
                                second: resetTime ? resetTime.second() : 0
                            }
                        }
                    }
                }
            };
            
            await API.patch(`${UPDATE_CONFIG}`, body);
            setIsShowModalConfirm(false);
            setIsShowModalSuccess(true);
            // Reset date states
            setDateFrom(null);
            setDateTo(null);
            // Reload config after successful update
            await getDataConfig();
        } catch (err) {
            setIsShowModalConfirm(false);
            // Show error message if needed
        }
    };

    const formatRequest = (dateFm) => {
        // If dateFm is just a date (YYYY-MM-DD), add time
        const dateStr = dateFm.includes(' ') ? dateFm : `${dateFm} 00:00:00`;
        const date = dayjs.tz(dateStr, 'YYYY-MM-DD HH:mm:ss', 'Asia/Bangkok');
        const output = date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        return output
    }

    const handleShowModal = () => {
        setIsShowModalConfirm(true);
    };

    const onChangeDate = (e, type) => {
        if (e) {
            const dateValue = dayjs(e).format('YYYY-MM-DD');
            if (type === "from") setDateFrom(dateValue);
            else setDateTo(dateValue);
        } else {
            if (type === "from") setDateFrom(null);
            else setDateTo(null);
        }
    };

    const checkFlagShow = () => {
        // Return isGameEnabled from configGame, default to false
        return configGame?.isGameEnabled === true;
    };

    const handleChangeSwitch = (checked) => {
        // Update isGameEnabled state when toggle switch changes
        setIsShowGame(checked);
    };

    const handleDayToggle = (dayValue, type) => {
        if (type === 'display') {
            // For display days, toggle day with default time
            const existingDay = displayDaysWithTime.find(d => d.day === dayValue);
            if (existingDay) {
                // Remove day
                setDisplayDaysWithTime(displayDaysWithTime.filter(d => d.day !== dayValue));
            } else {
                // Add day with default time
                setDisplayDaysWithTime([...displayDaysWithTime, { day: dayValue, time: dayjs("00:00:00", "HH:mm:ss") }]);
            }
        } else if (type === 'reset') {
            const existingDay = selectedResetDays.find(d => d.day === dayValue || d === dayValue);
            if (existingDay) {
                // Remove day
                setSelectedResetDays(selectedResetDays.filter(d => {
                    const day = typeof d === 'string' ? d : d.day;
                    return day !== dayValue;
                }));
            } else {
                // Add day with default time
                setSelectedResetDays([...selectedResetDays, { day: dayValue, time: "23:59:00" }]);
            }
        }
    };

    const handleTimeChange = (time, dayValue, type) => {
        if (type === 'display') {
            // For display days, update time for specific day
            // Check if time is a valid dayjs object (not null/undefined)
            if (time !== null && time !== undefined && dayValue) {
                setDisplayDaysWithTime(displayDaysWithTime.map(d => 
                    d.day === dayValue ? { ...d, time } : d
                ));
            }
        } else if (type === 'reset') {
            // Check if time is a valid dayjs object (not null/undefined)
            const timeStr = (time !== null && time !== undefined) ? dayjs(time).format('HH:mm:ss') : "23:59:00";
            setSelectedResetDays(selectedResetDays.map(d => {
                const day = typeof d === 'string' ? d : d.day;
                if (day === dayValue) {
                    return { day: dayValue, time: timeStr };
                }
                return typeof d === 'string' ? { day: d, time: "23:59:00" } : d;
            }));
        }
    };

    const renderDayButtons = (days, type) => {
        const isDaySelected = (dayValue) => {
            if (type === 'display') {
                // For display days, days is array of objects {day, time}
                return days.some(d => d.day === dayValue);
            } else {
                // For reset days, can be array of objects or strings
                return days.some(d => {
                    const day = typeof d === 'string' ? d : d.day;
                    return day === dayValue;
                });
            }
        };

        const getDayTime = (dayValue) => {
            if (type === 'display') {
                // For display days, get time from day object
                const dayObj = days.find(d => d.day === dayValue);
                return dayObj ? dayObj.time : null;
            } else {
                // For reset days, get time from day object
                const dayObj = days.find(d => {
                    const day = typeof d === 'string' ? d : d.day;
                    return day === dayValue;
                });
                if (!dayObj) return null;
                const time = typeof dayObj === 'string' ? "23:59:00" : dayObj.time;
                return dayjs(time, 'HH:mm:ss');
            }
        };

        return (
            <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                    {DAYS_SHORT.map((day) => {
                        const isSelected = isDaySelected(day.value);
                        return (
                            <button
                                key={day.value}
                                type="button"
                                onClick={() => handleDayToggle(day.value, type)}
                                className={`px-4 py-2 rounded ${
                                    isSelected
                                        ? "text-white"
                                        : "bg-gray-200 text-gray-700"
                                } transition-colors`}
                                style={{
                                    backgroundColor: isSelected ? "#1890ff" : "#e5e7eb",
                                    color: isSelected ? "#ffffff" : "#374151",
                                    textDecoration: "none",
                                    border: "none",
                                    cursor: "pointer",
                                }}
                            >
                                {day.label}
                            </button>
                        );
                    })}
                </div>
                {/* Time picker */}
                {(() => {
                    const selectedDaysList = DAYS_SHORT.filter(day => isDaySelected(day.value));
                    if (selectedDaysList.length === 0) return null;
                    
                    if (type === 'display') {
                        // For display days, show time picker for each selected day
                        return (
                            <div className="flex flex-col gap-3 mt-4 w-full" style={{ paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                                <div className="text-sm font-medium mb-2" style={{ color: "#374151" }}>{TEXT.time}:</div>
                                <div className="flex flex-wrap gap-4">
                                    {selectedDaysList.map((day) => {
                                        const dayTime = getDayTime(day.value) || dayjs("00:00:00", "HH:mm:ss");
                                        return (
                                            <div key={`time-${day.value}`} className="flex items-center gap-2" style={{ marginBottom: "8px" }}>
                                                <span style={{ minWidth: "40px", fontWeight: 500, color: "#374151" }}>{day.label}:</span>
                                                <TimePicker
                                                    value={dayTime}
                                                    onChange={(time) => handleTimeChange(time, day.value, type)}
                                                    format="HH:mm:ss"
                                                    style={{ width: "150px" }}
                                                    showNow={false}
                                                    allowClear={false}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    } else {
                        // For reset days, show time picker for each selected day
                        return (
                            <div className="flex flex-col gap-3 mt-4 w-full" style={{ paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                                <div className="text-sm font-medium mb-2" style={{ color: "#374151" }}>{TEXT.time}:</div>
                                <div className="flex flex-wrap gap-4">
                                    {selectedDaysList.map((day) => {
                                        const dayTime = getDayTime(day.value);
                                        return (
                                            <div key={`time-${day.value}`} className="flex items-center gap-2" style={{ marginBottom: "8px" }}>
                                                <span style={{ minWidth: "40px", fontWeight: 500, color: "#374151" }}>{day.label}:</span>
                                                <TimePicker
                                                    value={dayTime}
                                                    onChange={(time) => handleTimeChange(time, day.value, type)}
                                                    format="HH:mm:ss"
                                                    style={{ width: "150px" }}
                                                    showNow={false}
                                                    allowClear={false}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    }
                })()}
            </div>
        );
    };

    return (
        <div className="col-12">
            <div className="col-12">
                <div className="flex w-full">
                    <div className="flex flex-col w-full items-center justify-center">
                        <div className="col-10 mt-2 flex flex-col items-center justify-center">
                            {/* Section: Bật tắt game */}
                            <div className="w-full mb-6">
                                
                                {/* Toggle switch */}
                                <div className="flex w-full items-center">
                                    <div className="flex w-[20%] justify-end mr-10"></div>
                                    <div className="w-[60%] flex items-center gap-4">
                                        <Switch
                                            checkedChildren="On"
                                            unCheckedChildren="Off"
                                            checked={isShowGame !== undefined ? isShowGame : checkFlagShow()}
                                            style={{ width: 60 }}
                                            onChange={handleChangeSwitch}
                                        />
                                        <span className="ml-2">{TEXT.turnOnOff}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Event Time Configuration */}
                            <div className="w-full mb-6">
                                <div className="flex w-full items-center mb-4">
                                    <div className="flex w-[20%] justify-end mr-10">{TEXT.eventStartTime}:</div>
                                    <div className="w-[60%]">
                                        <DatePicker
                                            placeholder="dd/mm/yyyy"
                                            onChange={(e) => onChangeDate(e, "from")}                                      
                                            value={dateFrom ? dayjs(dateFrom) : (configGame?.eventStart ? dayjs(configGame.eventStart) : null)}
                                            format={"DD/MM/YYYY"}
                                            style={{ width: "100%" }}
                                            disabledDate={(d) =>
                                                !d ||
                                                (dateTo
                                                  ? d.isAfter(dayjs(dateTo), "day")
                                                  : false)
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="flex w-full items-center">
                                    <div className="flex w-[20%] justify-end mr-10">{TEXT.eventEndTime}:</div>
                                    <div className="w-[60%]">
                                        <DatePicker
                                            placeholder="dd/mm/yyyy"
                                            value={dateTo ? dayjs(dateTo) : (configGame?.eventEnd ? dayjs(configGame.eventEnd) : null)}
                                            onChange={(e) => onChangeDate(e, "to")}
                                            format={"DD/MM/YYYY"}
                                            style={{ width: "100%" }}
                                            disabledDate={(d) =>
                                                !d ||
                                                (dateFrom
                                                  ? d.isBefore(dayjs(dateFrom), "day")
                                                  : false)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Ngày hiển thị trong tuần */}
                            <div className="w-full mb-6">
                                <div className="flex w-full items-start mb-2">
                                    <div className="flex w-[20%] justify-end mr-10 pt-2">{TEXT.daysDisplayWeek}:</div>
                                    <div className="w-[60%]">
                                        {renderDayButtons(displayDaysWithTime, 'display')}
                                    </div>
                                </div>
                            </div>

                            {/* Ngày reset DTC */}
                            <div className="w-full mb-6">
                                <div className="flex w-full items-start">
                                    <div className="flex w-[20%] justify-end mr-10 pt-2">{TEXT.dtcResetDay}:</div>
                                    <div className="w-[60%]">
                                        <div className="flex flex-col gap-4">
                                            {/* Tần suất tabs */}
                                            <div>
                                                <Tabs
                                                    activeKey={resetFrequency}
                                                    onChange={(key) => {
                                                        setResetFrequency(key);
                                                        // When switching to weekly, ensure resetDay is between 1-7
                                                        if (key === "weekly" && resetDay > 7) {
                                                            setResetDay(7);
                                                        }
                                                        // When switching to monthly, ensure resetDay is between 1-31
                                                        if (key === "monthly" && resetDay > 31) {
                                                            setResetDay(31);
                                                        }
                                                    }}
                                                    items={[
                                                        {
                                                            key: "monthly",
                                                            label: TEXT.monthly,
                                                        },
                                                        {
                                                            key: "weekly",
                                                            label: TEXT.weekly,
                                                        },
                                                        {
                                                            key: "daily",
                                                            label: TEXT.daily,
                                                        },
                                                    ]}
                                                />
                                            </div>
                                            
                                            {/* Ngày reset và Giờ */}
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                    <span>{TEXT.resetDay}:</span>
                                                    <InputNumber
                                                        min={1}
                                                        max={resetFrequency === "weekly" ? 7 : 31}
                                                        value={resetDay}
                                                        onChange={(value) => {
                                                            // Ensure value is within valid range
                                                            if (resetFrequency === "weekly" && value > 7) {
                                                                setResetDay(7);
                                                            } else if (resetFrequency === "monthly" && value > 31) {
                                                                setResetDay(31);
                                                            } else {
                                                                setResetDay(value || 1);
                                                            }
                                                        }}
                                                        disabled={resetFrequency === "daily"}
                                                        style={{ width: "80px" }}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span>{TEXT.hour}:</span>
                                                    <TimePicker
                                                        value={resetTime}
                                                        onChange={setResetTime}
                                                        format="HH:mm:ss"
                                                        style={{ width: "150px" }}
                                                        showNow={false}
                                                        allowClear={false}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-10 mt-5 flex items-center justify-center">
                            <Button type="primary" onClick={handleShowModal}>
                                Cập nhật
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {isShowModalConfirm && (
                <ModalCommon
                    isShow={isShowModalConfirm}
                    message={TEXT_DEF.text0062}
                    txtBtnAcc={TEXT_DEF.text0008}
                    textBtnClose={TEXT_DEF.text0023}
                    actionNext={handleUpdateConfig}
                    close={() => setIsShowModalConfirm(false)}
                />
            )}
            {isShowModalSuccess && (
                <ModalCommon
                    isShow={isShowModalSuccess}
                    message={TEXT_DEF.text0063}
                    txtBtnAcc={TEXT_DEF.text0007}
                    actionNext={() => {
                        setIsShowModalSuccess(false);
                    }}
                    close={() => {
                        setIsShowModalSuccess(false);
                    }}
                />
            )}
        </div>
    );
};

export default OnOffGame;
